import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2, ArrowLeft, MessageSquare, Package, ChevronRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MessagesPage = () => {
  const { token, user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const T = (fr, en) => (language === 'fr' ? fr : en);

  const [conversations, setConversations] = useState([]);
  const [activeConvo, setActiveConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  // Load conversation list
  const fetchList = async () => {
    try {
      const r = await axios.get(`${API}/conversations`, { headers: { Authorization: `Bearer ${token}` } });
      setConversations(r.data || []);
    } catch (_) { /* silent */ } finally {
      setLoadingList(false);
    }
  };
  useEffect(() => { if (token) fetchList(); }, [token]);

  // Load active thread
  const loadThread = async (convoId) => {
    setLoadingThread(true);
    try {
      const r = await axios.get(`${API}/conversations/${convoId}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setActiveConvo(r.data.conversation);
      setMessages(r.data.messages || []);
      // refresh list (unread cleared)
      fetchList();
    } catch (_) { /* silent */ } finally {
      setLoadingThread(false);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 100);
    }
  };

  useEffect(() => {
    if (conversationId && token) loadThread(conversationId);
    // eslint-disable-next-line
  }, [conversationId, token]);

  const send = async (e) => {
    e?.preventDefault();
    if (!draft.trim() || !activeConvo) return;
    setSending(true);
    const content = draft.trim();
    setDraft('');
    // optimistic
    const tempId = `tmp-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, sender_id: user.id, sender_role: activeConvo.my_role || 'buyer', content, created_at: new Date().toISOString() }]);
    try {
      await axios.post(
        `${API}/conversations/${activeConvo.id}/messages`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // re-fetch (cleanest)
      const r = await axios.get(`${API}/conversations/${activeConvo.id}/messages`, { headers: { Authorization: `Bearer ${token}` } });
      setMessages(r.data.messages || []);
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight), 50);
    } catch (_) {
      // rollback
      setMessages((m) => m.filter((x) => x.id !== tempId));
      setDraft(content);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-main py-8 md:py-10" data-testid="messages-page">
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C5C] tracking-tight">
          {T('Messages', 'Messages')}
        </h1>
        <p className="text-muted-foreground mt-1">
          {T('Échangez avec les vendeurs et les acheteurs.', 'Chat with sellers and buyers.')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[340px_1fr] gap-6 bg-white rounded-2xl border border-[#0F4C5C]/10 overflow-hidden min-h-[60vh]">
        {/* Conversation list */}
        <aside className={`border-r border-[#0F4C5C]/10 ${activeConvo ? 'hidden md:block' : ''}`}>
          {loadingList ? (
            <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">
              <MessageSquare className="w-10 h-10 mx-auto text-[#0F4C5C]/30 mb-3" />
              {T('Aucune conversation pour l\'instant.', 'No conversations yet.')}
            </div>
          ) : (
            <div className="divide-y divide-[#0F4C5C]/5">
              {conversations.map((c) => {
                const isActive = activeConvo?.id === c.id;
                const otherName = c.my_role === 'buyer' ? c.vendor_name : c.buyer_name;
                return (
                  <button
                    key={c.id}
                    onClick={() => navigate(`/messages/${c.id}`)}
                    className={`w-full flex items-start gap-3 p-4 text-left transition-colors ${
                      isActive ? 'bg-[#FBF8F3]' : 'hover:bg-[#FBF8F3]/60'
                    }`}
                    data-testid={`conversation-${c.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#0F4C5C] text-white flex items-center justify-center font-semibold text-sm flex-shrink-0">
                      {(otherName || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-sm text-[#0F4C5C] truncate">{otherName}</p>
                        {c.my_unread > 0 && (
                          <span className="bg-[#E9C46A] text-[#0F4C5C] text-[10px] font-bold rounded-full min-w-[18px] h-4.5 px-1.5 flex items-center justify-center">
                            {c.my_unread}
                          </span>
                        )}
                      </div>
                      {c.product_snapshot?.name && (
                        <p className="text-xs text-[#E9C46A] font-medium flex items-center gap-1 mt-0.5">
                          <Package className="w-3 h-3" /> {c.product_snapshot.name}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </aside>

        {/* Thread */}
        <section className={`flex flex-col h-[60vh] md:h-[70vh] ${!activeConvo ? 'hidden md:flex' : 'flex'}`}>
          {!activeConvo ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8">
              {T('Sélectionnez une conversation', 'Select a conversation')}
            </div>
          ) : (
            <>
              {/* Header */}
              <header className="flex items-center gap-3 p-4 border-b border-[#0F4C5C]/10 bg-[#FBF8F3]/40">
                <button onClick={() => navigate('/messages')} className="md:hidden text-[#0F4C5C]" aria-label="Back">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-[#0F4C5C] text-white flex items-center justify-center font-semibold text-sm">
                  {((activeConvo.my_role === 'buyer' ? activeConvo.vendor_name : activeConvo.buyer_name) || '?').slice(0, 2).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#0F4C5C] truncate">
                    {activeConvo.my_role === 'buyer' ? activeConvo.vendor_name : activeConvo.buyer_name}
                  </p>
                  {activeConvo.product_snapshot && (
                    <button
                      onClick={() => navigate(`/product/${activeConvo.product_snapshot.id}`)}
                      className="text-xs text-[#E9C46A] hover:underline flex items-center gap-1"
                    >
                      <Package className="w-3 h-3" /> {activeConvo.product_snapshot.name}
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              </header>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#FBF8F3]/30" data-testid="messages-list">
                {loadingThread ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C5C]" /></div>
                ) : messages.length === 0 ? (
                  <div className="text-center text-sm text-muted-foreground py-8">{T('Pas encore de messages', 'No messages yet')}</div>
                ) : (
                  messages.map((m) => {
                    const isMine = m.sender_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                        <div
                          className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm ${
                            isMine
                              ? 'bg-[#0F4C5C] text-white rounded-br-sm'
                              : 'bg-white border border-[#0F4C5C]/10 text-[#0F4C5C] rounded-bl-sm'
                          }`}
                        >
                          <p className="whitespace-pre-wrap break-words">{m.content}</p>
                          <p className={`text-[10px] mt-1 ${isMine ? 'text-white/60' : 'text-[#0F4C5C]/50'}`}>
                            {new Date(m.created_at).toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Composer */}
              <form onSubmit={send} className="p-4 border-t border-[#0F4C5C]/10 bg-white flex gap-2">
                <Input
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder={T('Écrire un message...', 'Type a message...')}
                  className="h-11"
                  data-testid="message-input"
                />
                <Button type="submit" disabled={sending || !draft.trim()} className="btn-primary h-11 px-5" data-testid="message-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
};
